// src/components/MaintenanceLists/ReportingSection.js
import React from 'react';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Line } from 'react-chartjs-2';
import { calculateTCO, processMonthlyExpenses } from './TCOCalculator';
import { formatCurrency } from './constants';

/**
 * ReportingSection component for displaying TCO analysis and charts
 * 
 * @param {Object} props - Component props
 * @param {Object} props.bike - Bike data object
 * @param {boolean} props.isExpanded - Whether the reporting section is expanded
 * @param {Function} props.onToggleExpand - Function to toggle expansion state
 * @param {Function} props.onEditClick - Function to handle edit button click
 * @returns {JSX.Element|null} Rendered component or null if no purchase date
 */
const ReportingSection = ({ bike, isExpanded, onToggleExpand, onEditClick }) => {
  if (!bike.purchaseDate) return null;
  
  const tco = calculateTCO(bike);
  
  return (
    <div className="reporting-section">
      <div 
        className="reporting-header"
        onClick={onToggleExpand}
      >
        <h2>
          <FontAwesomeIcon 
            icon={faChevronRight} 
            style={{ 
              transform: isExpanded ? 'rotate(90deg)' : 'none',
              transition: 'transform 0.3s'
            }}
          />
          Reporting & Analytics
        </h2>
      </div>
      
      <div className={`reporting-content ${isExpanded ? 'expanded' : ''}`}>
        {bike.initialMileage !== null && bike.initialMileage !== undefined ? (
          <div className="tco-section">
            <div className="tco-categories">
              <div className="tco-header">Category Totals</div>
              <div className="tco-grid">
                <div className="tco-item">
                  <div className="tco-label">Maintenance Total</div>
                  <div className="tco-sublabel">Includes regular maintenance, repairs, and consumables</div>
                  <div className="tco-value">{formatCurrency(tco.maintenanceCosts)}</div>
                </div>
                <div className="tco-item">
                  <div className="tco-label">Operating Costs</div>
                  <div className="tco-sublabel">Insurance, road tax, and other running costs</div>
                  <div className="tco-value">{formatCurrency(tco.operatingCosts)}</div>
                </div>
                <div className="tco-item">
                  <div className="tco-label">Upgrades & Accessories</div>
                  <div className="tco-sublabel">Optional improvements and modifications</div>
                  <div className="tco-value">{formatCurrency(tco.upgradeCosts)}</div>
                </div>
                <div className="tco-item">
                  <div className="tco-label">Total All Categories</div>
                  <div className="tco-value highlight">{formatCurrency(tco.totalCostsWithUpgrades)}</div>
                </div>
              </div>
            </div>

            <div className="tco-analysis">
              <div className="tco-header">Cost of Ownership Analysis</div>
              <div className="tco-grid">
                <div className="tco-item">
                  <div className="tco-label">Ownership Period</div>
                  <div className="tco-value">
                    {Math.floor(tco.ownershipYears)} years, {Math.floor(tco.ownershipMonths % 12)} months
                  </div>
                </div>
                <div className="tco-item">
                  <div className="tco-label">Total Mileage</div>
                  <div className="tco-value">{tco.milesDriven.toLocaleString()} mi</div>
                </div>
                <div className="tco-item">
                  <div className="tco-label">Cost per Month</div>
                  <div className="tco-value">{formatCurrency(tco.monthlyTotal)}</div>
                </div>
                <div className="tco-item">
                  <div className="tco-label">Cost per Year</div>
                  <div className="tco-value highlight">{formatCurrency(tco.yearlyTotal)}</div>
                </div>
                {tco.costPerMile && (
                  <div className="tco-item">
                    <div className="tco-label">Cost per Mile</div>
                    <div className="tco-value">{formatCurrency(tco.costPerMile.total)}</div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="expenses-chart">
              {renderExpensesChart(bike)}
            </div>
          </div>
        ) : (
          <div className="tco-missing-data">
            <Button 
              variant="outline-secondary" 
              onClick={onEditClick}
            >
              Edit Details
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Render the expenses chart
 * 
 * @param {Object} bike - Bike data object
 * @returns {JSX.Element|null} Rendered chart or null if no data
 */
const renderExpensesChart = (bike) => {
  const monthlyData = processMonthlyExpenses(bike);
  if (!monthlyData) return null;

  const chartData = {
    labels: monthlyData.labels,
    datasets: [
      {
        label: 'Monthly Expenses',
        data: monthlyData.expenses,
        borderColor: '#0d6efd',
        backgroundColor: 'rgba(13, 110, 253, 0.5)',
        yAxisID: 'y',
      },
      {
        label: 'Cumulative Total',
        data: monthlyData.cumulative,
        borderColor: '#198754',
        backgroundColor: 'rgba(25, 135, 84, 0.5)',
        yAxisID: 'y1',
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'Monthly Expenses Over Time',
        color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color').trim()
      },
      legend: {
        labels: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color').trim()
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Monthly Spend (£)',
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color').trim()
        },
        ticks: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color').trim()
        },
        grid: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-border-color').trim()
        }
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Cumulative Total (£)',
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color').trim()
        },
        ticks: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color').trim()
        },
        grid: {
          drawOnChartArea: false,
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-border-color').trim()
        },
      },
      x: {
        ticks: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-body-color').trim()
        },
        grid: {
          color: getComputedStyle(document.documentElement).getPropertyValue('--bs-border-color').trim()
        }
      }
    },
  };

  return <Line options={options} data={chartData} />;
};

export default ReportingSection;